import { toFakeUtc } from "@framework/provider-asu";

import { formatTrendTooltipDate } from "../../../../../../../../utils";
import type { IChartDataSignal } from "../../interfaces";

type TSignalTooltipSeries = Pick<IChartDataSignal, "date" | "name" | "unitsX">;

interface IFormatSignalCascadeTooltipTitleArgs {
  data: readonly TSignalTooltipSeries[];
  datasetIndex: number;
  datasetLabel?: string;
  dbTz: number;
  displayCascade3D: boolean;
  shiftedX: number;
  xOffset: number;
  displayUnitsX?: string;
  formatXValue: (value: number, datasetIndex: number) => string;
}

export const formatSignalMeasurementDate = (date: string | undefined, dbTz: number): string | null => {
  if (!date) {
    return null;
  }

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return formatTrendTooltipDate(toFakeUtc(parsedDate, dbTz));
};

export const formatSignalCascadeTooltipTitle = ({
  data,
  datasetIndex,
  datasetLabel,
  dbTz,
  displayCascade3D,
  shiftedX,
  xOffset,
  displayUnitsX,
  formatXValue,
}: IFormatSignalCascadeTooltipTitleArgs): string | undefined => {
  if (!displayCascade3D || data.length <= 1) {
    return undefined;
  }

  const series = data[datasetIndex];
  const measurementDate = formatSignalMeasurementDate(series?.date, dbTz);

  if (!series || !measurementDate) {
    return undefined;
  }

  const xValue = shiftedX - datasetIndex * xOffset;
  const formattedX = formatXValue(xValue, datasetIndex);
  const unitsX = displayUnitsX ?? series.unitsX;
  const xWithUnits = unitsX ? `${formattedX} ${unitsX}` : formattedX;

  return `${datasetLabel ?? series.name}: ${xWithUnits}; ${measurementDate}`;
};

export const resolveSignalXDisplayScale = (
  series: Pick<IChartDataSignal, "unitsXCode" | "rotationFrequencyRpm"> | undefined,
  targetUnitsCode: number | undefined,
  fallback: number,
): number => {
  const sourceUnitsCode = series?.unitsXCode;
  if (sourceUnitsCode == null || targetUnitsCode == null) {
    return fallback;
  }
  if (sourceUnitsCode === targetUnitsCode) {
    return 1;
  }
  const rpm = series?.rotationFrequencyRpm;
  if (rpm == null || !Number.isFinite(rpm) || rpm <= 0) {
    return fallback;
  }
  if (sourceUnitsCode === 4 && targetUnitsCode === 3) {
    return rpm / 60;
  }
  if (sourceUnitsCode === 3 && targetUnitsCode === 4) {
    return 60 / rpm;
  }
  if (sourceUnitsCode === 6 && targetUnitsCode === 5) {
    return 60_000 / rpm;
  }
  if (sourceUnitsCode === 5 && targetUnitsCode === 6) {
    return rpm / 60_000;
  }
  return fallback;
};
